import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { observer } from 'mobx-react-lite';
import { callApi } from '../api/call_api';
import { useCallStore, useAuthStore } from '../stores/root_store';
import type { Call, CallParticipant } from '../api/call_api';

// Twilio Voice SDK types - will be properly typed when package is installed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Device = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TwilioCall = any;

export interface PhoneContextType {
  // State
  phoneReady: boolean;
  activeCallKey: string | null;
  activeCall: Call | undefined;
  activeCallParticipants: CallParticipant[];

  // Actions
  phoneMakeCall: (
    numberTo: string,
    associatedType: string,
    associatedUuid: string
  ) => Promise<Call | undefined>;
  phoneHangUp: () => void;
  phoneAddParticipant: (
    numberTo: string,
    callKey: string
  ) => Promise<CallParticipant | undefined>;
  phoneRemoveParticipant: (
    callKey: string,
    callSid: string
  ) => Promise<boolean>;
  sendDTMF: (tone: string) => void;

  // Data access
  getCallsByAssociation: (
    associatedType: string,
    associatedUuid: string
  ) => Call[];
  fetchCallsByAssociation: (
    associatedType: string,
    associatedUuid: string
  ) => Promise<void>;
  fetchCallParticipants: (callKey: string) => Promise<void>;
}

const PhoneContext = createContext<PhoneContextType | null>(null);

export const PhoneProvider = observer(function PhoneProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [phoneReady, setPhoneReady] = useState(false);
  const [twilioToken, setTwilioToken] = useState<string | null>(null);
  const [twilioCall, setTwilioCall] = useState<TwilioCall | null>(null);
  const [tokenLoaded, setTokenLoaded] = useState(false);

  const callStore = useCallStore();
  const authStore = useAuthStore();

  // Get identity key from auth store
  const identityKey = authStore.user?.uuid || null;

  // Twilio device reference - will be set when SDK is loaded
  const [device, setDevice] = useState<Device | null>(null);

  // Load Twilio SDK dynamically when we have a token
  // Note: Install @twilio/voice-sdk to enable phone features
  useEffect(() => {
    if (!twilioToken) {
      setDevice(null);
      return;
    }

    // Try to load Twilio SDK - will fail gracefully if not installed
    const loadTwilio = async () => {
      try {
        const { Device } = await import('@twilio/voice-sdk');
        const newDevice = new Device(twilioToken);
        setDevice(newDevice);
      } catch (err) {
        console.warn(
          'Twilio Voice SDK not available. Install @twilio/voice-sdk to enable phone features.',
          err
        );
        setDevice(null);
      }
    };

    loadTwilio();

    return () => {
      // Cleanup device on unmount
      if (device) {
        device.destroy?.();
      }
    };
  }, [twilioToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch token on mount
  const fetchToken = useCallback(async () => {
    if (tokenLoaded || !identityKey) {
      return;
    }

    setTokenLoaded(true);
    try {
      const data = await callApi.getToken(identityKey);
      setTwilioToken(data.jwt);
    } catch (err) {
      console.error('Failed to get Twilio token:', err);
    }
  }, [identityKey, tokenLoaded]);

  // Set up device event listeners
  useEffect(() => {
    if (!device) return;

    device.addListener('registered', () => {
      // Device ready to receive calls
    });

    device.on('incoming', (call: TwilioCall) => {
      // Handle incoming call
      console.log('Incoming call:', call);
    });

    device.on('disconnect', () => {
      setTwilioCall(null);
      callStore.setActiveCall(null);
    });

    device.on('tokenWillExpire', () => {
      // Could refresh token here
    });

    setPhoneReady(true);

    return () => {
      device.destroy();
      setPhoneReady(false);
    };
  }, [device, callStore]);

  // Fetch token when identity is available
  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  // Make a call
  const phoneMakeCall = useCallback(
    async (
      numberTo: string,
      associatedType: string,
      associatedUuid: string
    ): Promise<Call | undefined> => {
      if (!identityKey || !device) {
        console.error('Cannot make call: no identity or device');
        return undefined;
      }

      try {
        const call = await callStore.createCall(
          numberTo,
          associatedType,
          associatedUuid,
          identityKey
        );

        if (!call) return undefined;

        // Connect via Twilio
        const twilioConnection = await device.connect({
          params: { To: numberTo, call: call.key },
        });

        setTwilioCall(twilioConnection);

        // Add the participant after a delay (Twilio needs time to set up)
        setTimeout(() => {
          callStore.addParticipant(numberTo, call.key, identityKey);
        }, 4000);

        return call;
      } catch (err) {
        console.error('Failed to make call:', err);
        return undefined;
      }
    },
    [identityKey, device, callStore]
  );

  // Hang up
  const phoneHangUp = useCallback(() => {
    if (device) {
      device.disconnectAll();
    }
    setTwilioCall(null);
    callStore.setActiveCall(null);
  }, [device, callStore]);

  // Add participant
  const phoneAddParticipant = useCallback(
    async (
      numberTo: string,
      callKey: string
    ): Promise<CallParticipant | undefined> => {
      if (!identityKey) return undefined;
      return callStore.addParticipant(numberTo, callKey, identityKey);
    },
    [identityKey, callStore]
  );

  // Remove participant
  const phoneRemoveParticipant = useCallback(
    async (callKey: string, callSid: string): Promise<boolean> => {
      if (!identityKey) return false;
      return callStore.removeParticipant(callKey, callSid, identityKey);
    },
    [identityKey, callStore]
  );

  // Send DTMF tones
  const sendDTMF = useCallback(
    (tone: string) => {
      if (twilioCall) {
        twilioCall.sendDigits(tone);
      }
    },
    [twilioCall]
  );

  // Get calls by association
  const getCallsByAssociation = useCallback(
    (associatedType: string, associatedUuid: string): Call[] => {
      return callStore.getCallsByAssociation(associatedType, associatedUuid);
    },
    [callStore]
  );

  // Fetch calls by association
  const fetchCallsByAssociation = useCallback(
    async (associatedType: string, associatedUuid: string): Promise<void> => {
      if (!identityKey) return;
      await callStore.fetchByAssociation(
        associatedType,
        associatedUuid,
        identityKey
      );
    },
    [identityKey, callStore]
  );

  // Fetch call participants
  const fetchCallParticipants = useCallback(
    async (callKey: string): Promise<void> => {
      if (!identityKey) return;
      await callStore.fetchParticipants(callKey, identityKey);
    },
    [identityKey, callStore]
  );

  const value: PhoneContextType = {
    phoneReady,
    activeCallKey: callStore.activeCallKey,
    activeCall: callStore.activeCall,
    activeCallParticipants: callStore.activeCallParticipants,
    phoneMakeCall,
    phoneHangUp,
    phoneAddParticipant,
    phoneRemoveParticipant,
    sendDTMF,
    getCallsByAssociation,
    fetchCallsByAssociation,
    fetchCallParticipants,
  };

  return (
    <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export function usePhone(): PhoneContextType {
  const context = useContext(PhoneContext);
  if (!context) {
    throw new Error('usePhone must be used within a PhoneProvider');
  }
  return context;
}
